import { Controller } from "@hotwired/stimulus";

/**
 * Clipboard controller for copying text to clipboard with visual feedback.
 *
 * Usage (legacy - uses hidden class):
 *   <button data-controller="clipboard"
 *           data-clipboard-text-value="text to copy"
 *           data-action="click->clipboard#copy">
 *     <span data-clipboard-target="icon">Copy icon</span>
 *     <span data-clipboard-target="success" class="hidden">Check icon</span>
 *   </button>
 *
 * Usage (animated - uses copy-feedback-* classes):
 *   <button data-controller="clipboard"
 *           data-clipboard-text-value="text to copy"
 *           data-action="click->clipboard#copy">
 *     <span data-clipboard-target="icon" class="copy-feedback-icon">Copy icon</span>
 *     <span data-clipboard-target="success" class="copy-feedback-success">Check icon</span>
 *   </button>
 */
export default class ClipboardController extends Controller {
  static values = {
    text: String,
  };

  static targets = ["icon", "success"];

  declare textValue: string;
  declare iconTarget: HTMLElement;
  declare successTarget: HTMLElement;
  declare hasIconTarget: boolean;
  declare hasSuccessTarget: boolean;

  async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.textValue);
      this.showSuccess();
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }

  private showSuccess(): void {
    if (this.hasIconTarget && this.hasSuccessTarget) {
      // Check if using animated classes (copy-feedback-*)
      const isAnimated = this.iconTarget.classList.contains("copy-feedback-icon");

      if (isAnimated) {
        this.iconTarget.classList.add("copying");
        this.successTarget.classList.add("copying");

        setTimeout(() => {
          this.iconTarget.classList.remove("copying");
          this.successTarget.classList.remove("copying");
        }, 2000);
      } else {
        // Legacy behavior with hidden class
        this.iconTarget.classList.add("hidden");
        this.successTarget.classList.remove("hidden");

        setTimeout(() => {
          this.iconTarget.classList.remove("hidden");
          this.successTarget.classList.add("hidden");
        }, 2000);
      }
    }
  }
}
